import { useCallback, useEffect, useMemo, useState } from 'react'
import useSWRInfinite from 'swr/infinite'

import ChatflowsApi, { chatflowListResponseSchema } from '../api/agentBuilder/chatflows'
import useApiClient from './useApiClient'

export interface TeammateSearchOption {
  id: string
  label: string
  description?: string
}

const SEARCH_PAGE_SIZE = 25
const DEBOUNCE_MS = 300

/**
 * Server-side Teammate (agent chatflow) search behind the Teams Builder step picker. The search term
 * is sent to `GET /chatflows` so results are never limited to whatever the client happened to preload,
 * and `show: 'accessible'` scopes the list to teammates the caller owns or that are shared with them /
 * their groups (never the whole org for admins) (WF-2583).
 *
 * Results are paginated rather than capped at the first page: the caller drives `loadMore()` from the
 * listbox's scroll handler and the loaded pages are flattened into `results`. `numResults` is the
 * server's total match count (taken before the page's skip/take), so `hasMore` is exact and the list
 * never requests an empty page (WF-2751).
 *
 * The request is keyed on the (debounced, trimmed) search term via SWR, so every mounted picker that
 * shares a term shares a single request. A team with N step dropdowns therefore fires one GET per page
 * instead of N identical ones. Because the term is part of the first page's key, SWR resets the page
 * count itself whenever the term changes, so a new search always starts from page 1. The visible input
 * can be controlled (multi-select pickers clear it after each pick) or left uncontrolled; either way
 * `setSearchInput` drives the debounced fetch.
 */
export default function useTeammateSearch() {
  const apiClientContext = useApiClient()
  const chatflowsApi = useMemo(() => new ChatflowsApi(apiClientContext.torqAgentClient), [apiClientContext.torqAgentClient])

  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  const filter = debouncedSearch.trim()

  const { data, isLoading, isValidating, size, setSize } = useSWRInfinite(
    (pageIndex: number) => ['teammate-search', filter, pageIndex + 1] as const,
    async ([, term, pageNumber]) => {
      const res = await chatflowsApi.getChatflowsPaginated({
        chatFlowType: 'agent',
        filter: term || undefined,
        pageSize: SEARCH_PAGE_SIZE,
        pageNumber,
        orderBy: 'name',
        orderDirection: 'ASC',
        show: 'accessible',
      })
      return chatflowListResponseSchema.parse(res.data)
    },
    // keepPreviousData shows the prior results while a new search is in flight instead of flashing
    // empty; revalidateOnFocus is off because the list rarely changes mid-edit; revalidateFirstPage is
    // off so appending page N doesn't re-request every earlier page on each scroll.
    { revalidateOnFocus: false, revalidateFirstPage: false, keepPreviousData: true }
  )

  const pages = useMemo(() => (data ?? []).filter(page => page !== undefined), [data])

  const results = useMemo<TeammateSearchOption[]>(() => {
    const seen = new Set<string>()
    const options: TeammateSearchOption[] = []
    pages.forEach(page => {
      page.chatflows.forEach(cf => {
        // A teammate created or renamed between page requests can land on two pages; MUI throws on
        // duplicate option keys, so keep only the first occurrence.
        if (seen.has(cf.id)) return
        seen.add(cf.id)
        options.push({ id: cf.id, label: cf.name, description: cf.description ?? undefined })
      })
    })
    return options
  }, [pages])

  // A requested page that hasn't arrived yet means we're appending, not re-searching, so the caller
  // can keep the visible options on screen instead of blanking the list behind a search spinner.
  const isLoadingMore = size > pages.length
  const isSearching = isLoading || (isValidating && !isLoadingMore)

  const lastPage = pages[pages.length - 1]
  const loadedCount = pages.reduce((total, page) => total + page.chatflows.length, 0)
  // The short-page guard is a fallback for the case where rows disappear mid-scroll and numResults no
  // longer matches what the pages can actually return.
  const hasMore = lastPage !== undefined && loadedCount < lastPage.numResults && lastPage.chatflows.length > 0

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return
    void setSize(current => current + 1)
  }, [hasMore, isLoadingMore, setSize])

  // Clearing drops the debounced term immediately instead of 300ms later, so the listbox snaps back to
  // the unfiltered first page rather than briefly re-showing the search that was just cleared (WF-2751).
  const resetSearch = useCallback(() => {
    setSearchInput('')
    setDebouncedSearch('')
  }, [])

  return { searchInput, setSearchInput, debouncedSearch, results, isSearching, isLoadingMore, hasMore, loadMore, resetSearch }
}
